import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight,
  BookOpen,
  Check,
  Landmark,
  Lock,
  Shield,
  Camera,
  Scale,
  HeartHandshake,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PLANS } from '../data/plans';
import PartnerLibrariesMap from '../components/PartnerLibrariesMap';

interface PopularBook {
  id: string;
  title: string;
  author: string;
  cover_image?: string;
}

const Home: React.FC = () => {
  const { user } = useAuth();
  const [books, setBooks] = useState<PopularBook[]>([]);

  useEffect(() => {
    axios
      .get('/api/books/popular')
      .then((res) => setBooks((res.data || []).slice(0, 4)))
      .catch(() => setBooks([]));
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <p className="section-label mb-4">Ville pilote · Bruxelles</p>
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.15] text-ink">
                Prêtez vos livres.
                <br />
                Récupérez-les.
              </h1>
              <p className="mt-6 text-lg text-ink-muted max-w-xl leading-relaxed">
                BookShare fait circuler les livres déjà lus, y compris ceux que l’on n’ose pas
                confier à un inconnu. La caution est bloquée. La remise se fait dans une
                bibliothèque, pas au coin d’une rue.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {user ? (
                  <Link to="/library" className="btn btn-primary">
                    Ouvrir le catalogue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                ) : (
                  <>
                    <Link to="/register" className="btn btn-primary">
                      Essayer un emprunt
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                    <a href="#tarifs" className="btn btn-outline">
                      Voir les tarifs
                    </a>
                  </>
                )}
              </div>
              <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-muted">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-forest-600" /> Un emprunt gratuit, une seule fois
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-forest-600" /> Caution jusqu’au retour validé
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-forest-600" /> Lieux ouverts à Bruxelles
                </li>
              </ul>
            </div>
            <div className="lg:col-span-5">
              <div className="card p-5">
                <p className="text-xs font-semibold tracking-widest uppercase text-ink-muted mb-4">
                  Catalogue en circulation
                </p>
                <div className="space-y-3">
                  {books.length === 0 && (
                    <p className="text-sm text-ink-muted">Les premiers titres apparaîtront ici.</p>
                  )}
                  {books.map((book) => (
                    <div key={book.id} className="flex items-center gap-3">
                      {book.cover_image ? (
                        <img
                          src={book.cover_image}
                          alt=""
                          className="h-14 w-10 object-cover rounded-md border border-gray-200"
                        />
                      ) : (
                        <div className="h-14 w-10 rounded-md bg-paper-dark flex items-center justify-center">
                          <BookOpen className="h-4 w-4 text-ink-muted" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{book.title}</p>
                        <p className="text-xs text-ink-muted truncate">{book.author}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid sm:grid-cols-3 gap-8 text-sm">
          <div>
            <p className="font-serif text-2xl text-ink">Complément</p>
            <p className="mt-2 text-ink-muted leading-relaxed">
              La bibliothèque publique reste le plus économique pour les titres courants. BookShare
              sert les collections privées et les ouvrages introuvables.
            </p>
          </div>
          <div>
            <p className="font-serif text-2xl text-ink">Pas 1–2 € le livre</p>
            <p className="mt-2 text-ink-muted leading-relaxed">
              En Belgique, l’inscription communale vaut souvent 0 à 6 €/an, et l’emprunt 0,20 à
              0,50 € — parfois gratuit à Bruxelles. Nous ne prétendons pas être moins chers.
            </p>
          </div>
          <div>
            <p className="font-serif text-2xl text-ink">3,90 € / mois</p>
            <p className="mt-2 text-ink-muted leading-relaxed">
              Un roman neuf : environ 18 €. L’abonnement Lecteur couvre la caution, la médiation et
              les lieux de remise. Moins qu’un livre acheté, plus qu’un prêt public.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="section-label mb-3">Déroulement</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-12 max-w-2xl">
            Un prêt, cinq étapes. Rien ne se joue dans la rue.
          </h2>
          <ol className="grid md:grid-cols-5 gap-6">
            {[
              { n: '01', t: 'Déposer', d: 'Vous listez un livre avec son état et, si besoin, sa valeur.' },
              { n: '02', t: 'Demander', d: 'L’emprunteur choisit un point de remise parmi les bibliothèques pilotes.' },
              { n: '03', t: 'Caution', d: 'Un montant est bloqué jusqu’à validation du retour.' },
              { n: '04', t: 'Remise', d: 'Échange dans le hall d’une bibliothèque, aux heures d’ouverture.' },
              { n: '05', t: 'Retour', d: 'Le propriétaire confirme l’état. La caution est libérée ou retenue.' },
            ].map((step) => (
              <li key={step.n} className="border-t border-gray-300 pt-4">
                <p className="text-xs text-ink-faint mb-2">{step.n}</p>
                <h3 className="font-medium text-ink mb-2">{step.t}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{step.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="confiance" className="py-20 bg-navy-700 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-white/60 mb-3">
            Protection BookShare
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-4 max-w-2xl">
            Inspiré de la protection acheteur, adapté au prêt.
          </h2>
          <p className="text-white/75 max-w-2xl mb-12 leading-relaxed">
            Un livre rare ou coûteux n’entre dans le circuit que si la caution, l’identité et le
            lieu de remise sont en place. Le propriétaire décide ; la plateforme archive.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Lock,
                t: 'Caution bloquée',
                d: 'Indexée sur la valeur et l’état. Libérée uniquement après retour conforme.',
              },
              {
                icon: Camera,
                t: 'État photographié',
                d: 'Photos à la remise et au retour. En cas de litige, elles font foi.',
              },
              {
                icon: Landmark,
                t: 'Lieu identifié',
                d: 'Pas de rendez-vous improvisé. Un bâtiment public, ouvert, fréquenté.',
              },
              {
                icon: Scale,
                t: 'Médiation',
                d: 'Perte, dégât ou retard : la plateforme tranche selon la charte, pas un arrangement privé.',
              },
            ].map((item) => (
              <div key={item.t} className="border border-white/15 rounded-xl p-5">
                <item.icon className="h-5 w-5 mb-4 text-white/80" />
                <h3 className="font-medium mb-2">{item.t}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-white/60 max-w-3xl">
            Un compte récent ne peut pas emprunter un ouvrage de forte valeur. La garantie
            financière est obligatoire au-delà d’un seuil. Le paiement réel (empreinte bancaire)
            sera branché avant l’ouverture payante ; jusqu’ici, le parcours et les règles sont déjà
            ceux de la version finale.
          </p>
          <Link
            to="/legal/garantie"
            className="inline-flex items-center mt-6 text-sm text-white border-b border-white/40 hover:border-white"
          >
            Lire la charte de garantie
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>

      <section id="lieux" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="section-label mb-3">Remise</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-4">
            Bruxelles, ville pilote.
          </h2>
          <p className="text-ink-muted max-w-2xl mb-10 leading-relaxed">
            Les échanges se font dans des bibliothèques publiques — des lieux de confiance, pas un
            parking ni un café de passage. Autorisez la localisation pour voir les points de remise
            les plus proches ; sinon nous partons du centre de Bruxelles.
          </p>
          <PartnerLibrariesMap />
          <div className="mt-8 flex items-start gap-3 text-sm text-ink-muted max-w-2xl">
            <HeartHandshake className="h-5 w-5 text-forest-600 shrink-0 mt-0.5" />
            <p>
              Vous dirigez une bibliothèque bruxelloise ? Hall uniquement, zéro charge de guichet.{' '}
              <Link to="/partenaires" className="text-forest-700 underline underline-offset-2">
                Pourquoi devenir point de remise
              </Link>
              {' · '}
              <Link to="/partenaires/dossier" className="text-forest-700 underline underline-offset-2">
                Dossier 1 page
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <section id="tarifs" className="py-20 bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="section-label mb-3">Tarifs</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-3">
            Un essai. Puis un abonnement sobre.
          </h2>
          <p className="text-ink-muted max-w-2xl mb-12 leading-relaxed">
            Le compte gratuit n’offre pas un emprunt par mois : il offre un emprunt, une fois, pour
            juger du service. Lister ses livres reste gratuit. Le prêteur ne paie pas pour déposer
            un titre.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <article
                key={plan.id}
                className={`rounded-2xl border p-6 flex flex-col ${
                  plan.featured
                    ? 'border-forest-600 bg-forest-50/40 shadow-card'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {plan.featured && (
                  <p className="text-xs font-semibold tracking-widest uppercase text-forest-700 mb-3">
                    Recommandé
                  </p>
                )}
                <h3 className="font-serif text-2xl text-ink">{plan.name}</h3>
                <p className="mt-4">
                  <span className="text-3xl font-medium text-ink">{plan.price}</span>
                  <span className="text-sm text-ink-muted"> {plan.period}</span>
                </p>
                {plan.annual && <p className="text-xs text-ink-muted mt-1">ou {plan.annual}</p>}
                <p className="text-sm text-ink-muted mt-4 leading-relaxed">{plan.description}</p>
                <ul className="mt-6 space-y-2 text-sm text-ink flex-1">
                  {plan.features.map((point) => (
                    <li key={point} className="flex gap-2">
                      <Check className="h-4 w-4 text-forest-600 shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
                {user ? (
                  <Link
                    to={plan.id === 'free' ? '/library' : '/profile'}
                    className={`btn mt-8 w-full ${plan.featured ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {plan.id === 'free' ? 'Continuer' : 'Préinscription'}
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className={`btn mt-8 w-full ${plan.featured ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {plan.cta}
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl text-ink mb-8">Questions fréquentes</h2>
          <dl className="space-y-8">
            <div>
              <dt className="font-medium text-ink">En quoi est-ce différent d’une bibliothèque ?</dt>
              <dd className="mt-2 text-sm text-ink-muted leading-relaxed">
                La bibliothèque prête un fonds public, souvent pour quelques euros par an. BookShare
                fait circuler des exemplaires privés. Les deux peuvent coexister : nous nous
                appuyons même sur les bibliothèques comme lieux de remise.
              </dd>
            </div>
            <div>
              <dt className="font-medium text-ink">Que se passe-t-il si mon livre est abîmé ou perdu ?</dt>
              <dd className="mt-2 text-sm text-ink-muted leading-relaxed">
                Vous signalez un retour non conforme. La caution peut être retenue en tout ou
                partie, selon la charte. Un livre de valeur n’est proposable que si la caution
                correspond.
              </dd>
            </div>
            <div>
              <dt className="font-medium text-ink">Pourquoi un abonnement plutôt qu’un prix par livre ?</dt>
              <dd className="mt-2 text-sm text-ink-muted leading-relaxed">
                Un tarif à l’ouvrage nous alignerait mal avec le prêt public (souvent moins de 50
                centimes). L’abonnement finance la protection, pas le droit de lire un titre isolé.
              </dd>
            </div>
            <div>
              <dt className="font-medium text-ink">Le premier emprunt est-il vraiment unique ?</dt>
              <dd className="mt-2 text-sm text-ink-muted leading-relaxed">
                Oui. Une demande d’emprunt active ou aboutie consomme l’essai. Lister des livres,
                en revanche, n’est pas limité.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-forest-800 text-white px-8 py-12 sm:px-12">
            <div className="flex items-start gap-4 max-w-2xl">
              <Shield className="h-6 w-6 shrink-0 mt-1" />
              <div>
                <h2 className="font-serif text-3xl mb-3">Testez un emprunt. Un seul, sans abonnement.</h2>
                <p className="text-white/75 leading-relaxed">
                  Créez un compte, choisissez un livre à Bruxelles, convenez d’une bibliothèque.
                  C’est suffisant pour juger si l’outil mérite d’exister.
                </p>
                <Link
                  to={user ? '/library' : '/register'}
                  className="inline-flex items-center mt-6 bg-white text-forest-800 px-5 py-2.5 rounded-lg font-medium hover:bg-paper"
                >
                  {user ? 'Aller au catalogue' : 'Créer un compte'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
